import React, { useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  CButton,
  CCallout,
  CFormLabel,
  CFormSelect,
  CSmartTable,
} from "@coreui/react-pro";
import EmployerPageFrame from "../../components/employer/EmployerPageFrame";
import useEmployerOsWorkspace from "../../hooks/useEmployerOsWorkspace";
import { buildCandidateCaseRows, buildIntegrationRows, buildGateSummary } from "../../services/employer/employerCaseOsDomain";
import { formatEmployerAdFocusLabel } from "../../utils/employerAdLabels";

export default function EmployerIntegrationPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const workspace = useEmployerOsWorkspace({ preloadDetails: true, preloadFiling: true });
  const requestedAdId = searchParams.get("adId") || "";

  useEffect(() => {
    if (requestedAdId) workspace.setSelectedAdId(requestedAdId);
  }, [requestedAdId, workspace.setSelectedAdId]);

  const caseRows = useMemo(
    () => buildCandidateCaseRows({
      ads: workspace.ads,
      applications: workspace.applications,
      detailsById: workspace.detailsById,
      filingById: workspace.filingById,
    }),
    [workspace.ads, workspace.applications, workspace.detailsById, workspace.filingById]
  );
  const integrationRows = useMemo(
    () => buildIntegrationRows({ caseRows, profile: workspace.profile || {} }),
    [caseRows, workspace.profile]
  );
  const gateSummary = useMemo(() => buildGateSummary(workspace.profile || {}), [workspace.profile]);

  return (
    <EmployerPageFrame
      title="Integration"
      subtitle="Arrival, erste 100 Tage, Eskalationen und spaetere Retention-Signale docken jetzt an dieselbe Case- und Gate-Logik an statt ein separates Tool zu bilden."
      actions={(
        <>
          <CButton color="secondary" variant="outline" onClick={() => workspace.refreshWorkspace()}>
            Aktualisieren
          </CButton>
          <CButton color="primary" onClick={() => navigate("/dashboard/employer/profile")}>
            Gate pflegen
          </CButton>
        </>
      )}
      contentClassName="space-y-4"
      shellProps={{
        shellLabel: "Employer Integration OS",
        shellDescription: "Arrival, Integration und spaetere Retention nutzen dieselben Cases, nicht eine Nebenwelt.",
        topContext: "Integration",
      }}
    >
      <section className="dashboard-card-shell space-y-4">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <span className="dashboard-kicker">Arrival & 100 Days</span>
            <h2 className="dashboard-card-title">Integrationsbereitschaft ist jetzt Teil des Employer OS</h2>
            <p className="dashboard-section-copy">Unterkunft, Gehaltsbruecke, Owner und Teamvorbereitung werden nicht spaeter irgendwo angeflanscht, sondern heute schon als Case-Voraussetzung mitgefuehrt.</p>
          </div>
          <div className="min-w-[260px]">
            <CFormLabel>Anzeige</CFormLabel>
            <CFormSelect
              value={workspace.selectedAdId}
              onChange={(event) => workspace.setSelectedAdId(event.target.value)}
              options={(workspace.ads || []).map((entry) => ({
                label: formatEmployerAdFocusLabel(entry),
                value: String(entry.id),
              }))}
            />
          </div>
        </div>

        <CCallout color={gateSummary.status === "complete" ? "success" : "warning"}>
          <strong>{gateSummary.statusLabel}</strong>
          <div>
            {gateSummary.status === "complete"
              ? "Der Arbeitgeber hat die fuer Arrival und Onboarding verbindlichen Grundlagen sichtbar dokumentiert."
              : "Integration bleibt noch unvollstaendig. Bevor Retention oder Arrival skaliert werden, muessen Gate-Luecken im Arbeitgeberprofil geschlossen werden."}
          </div>
        </CCallout>

        {integrationRows.length ? (
          <CSmartTable
            columns={[
              { key: "candidateRef", label: "Candidate" },
              { key: "adTitle", label: "Job" },
              { key: "statusLabel", label: "Status" },
              { key: "housingReady", label: "Unterkunft" },
              { key: "salaryBridgeReady", label: "Gehaltsbruecke" },
              { key: "integrationOwner", label: "Owner" },
              { key: "teamReady", label: "Team" },
              { key: "milestone", label: "Milestone" },
              { key: "nextAction", label: "Naechste Aktion" },
            ]}
            items={integrationRows}
            pagination={false}
            sorter={false}
            tableProps={{ hover: true, responsive: true }}
          />
        ) : (
          <CCallout color="info">
            <strong>Noch keine Integrations-Cases im Fokus.</strong>
            <div>Sobald Kandidaten in der Arbeitsfläche vorhanden sind, wird Integration direkt an denselben Cases sichtbar.</div>
          </CCallout>
        )}
      </section>
    </EmployerPageFrame>
  );
}
